/**
 * UnitGuideEditorContent
 *
 * Extracted, self-contained unit guide editor. Accepts a courseId prop.
 * Used by:
 *   - UnitGuideTab (inline in #/curriculum)
 *   - UnitGuideEditorModal (modal in #/settings)
 */
import React, { useEffect, useMemo, useState } from "react";
import { unitAPI, unitGuideAPI } from "../../services/api";
import { unitGuides as hardcodedUnitGuides } from "../../data/unitGuides";
import "../UnitGuideEditorModal.css";

// ---- Helpers (same as original modal) ----

const sortGuides = (rows) =>
  [...rows].sort(
    (a, b) => Number(a.unitNumber || 0) - Number(b.unitNumber || 0)
  );

const listToText = (items) =>
  Array.isArray(items) ? items.join("\n") : "";

const textToList = (value) =>
  String(value || "")
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);

const toDateInput = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  return raw.includes("T") ? raw.split("T")[0] : raw;
};

const formatDateRangePreview = (startDate, endDate) => {
  if (!startDate || !endDate) return "";
  const parse = (v) => new Date(`${v}T00:00:00`);
  const start = parse(startDate);
  const end = parse(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  const options = { month: "short", day: "numeric", year: "numeric" };
  return `${start.toLocaleDateString("en-US", options)} to ${end.toLocaleDateString("en-US", options)}`;
};

const toDraft = (guide) => ({
  courseId: guide.courseId || "",
  unitNumber: String(guide.unitNumber ?? ""),
  title: guide.title || "",
  subtitle: guide.subtitle || "",
  startDate: toDateInput(guide.startDate),
  endDate: toDateInput(guide.endDate),
  dateRange: guide.dateRange || "",
  totalDays: guide.totalDays || "",
  sourceLabel: guide.sourceLabel || "",
  sourceUrl: guide.sourceUrl || "",
  transcriptPath: guide.transcriptPath || "",
  sections: Array.isArray(guide.sections)
    ? guide.sections.map((section) => ({
        id: section.id || "",
        heading: section.heading || "",
        paragraphsText: listToText(section.paragraphs),
        bulletsText: listToText(section.bullets),
      }))
    : [],
});

const nextSectionId = (sections) => {
  const existing = new Set(sections.map((s) => s.id));
  let index = sections.length + 1;
  let candidate = `section-${index}`;
  while (existing.has(candidate)) {
    index += 1;
    candidate = `section-${index}`;
  }
  return candidate;
};

const templateGuideSource = hardcodedUnitGuides[0] || null;

const toPositiveUnitNumber = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

const suggestTemplateUnitNumber = (courseUnits = [], guides = []) => {
  const unitNumbers = [
    ...new Set(
      (courseUnits || [])
        .map((unit) =>
          toPositiveUnitNumber(unit?.unit_number ?? unit?.unitNumber)
        )
        .filter(Boolean)
    ),
  ].sort((a, b) => a - b);

  if (unitNumbers.length === 0) return 1;

  const guideUnitSet = new Set(
    (guides || [])
      .map((g) => toPositiveUnitNumber(g?.unitNumber))
      .filter(Boolean)
  );

  const firstMissing = unitNumbers.find((n) => !guideUnitSet.has(n));
  if (firstMissing) return firstMissing;
  return Math.max(...unitNumbers) + 1;
};

const buildTemplateDraft = (courseId, unitNumber = 1) => {
  const templateSections =
    Array.isArray(templateGuideSource?.sections) &&
    templateGuideSource.sections.length > 0
      ? templateGuideSource.sections.map((section, index) => ({
          id: section.id || `section-${index + 1}`,
          heading: section.heading || `Section ${index + 1}`,
          paragraphsText: listToText(section.paragraphs),
          bulletsText: listToText(section.bullets),
        }))
      : [
          {
            id: "overview",
            heading: "Unit Overview",
            paragraphsText: "",
            bulletsText: "",
          },
        ];

  return {
    courseId: courseId || "",
    unitNumber: String(unitNumber),
    title: `Unit ${unitNumber}: New Guide`,
    subtitle: templateGuideSource?.subtitle || "Curriculum Unit Guide",
    startDate: "",
    endDate: "",
    dateRange: "",
    totalDays: "",
    sourceLabel: "",
    sourceUrl: "",
    transcriptPath: "",
    sections: templateSections,
  };
};

// ---- Component ----

const UnitGuideEditorContent = ({ courseId }) => {
  const [guides, setGuides] = useState([]);
  const [courseUnits, setCourseUnits] = useState([]);
  const [selectedGuideId, setSelectedGuideId] = useState("");
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  // Reload when courseId changes
  useEffect(() => {
    if (!courseId) {
      setGuides([]);
      setCourseUnits([]);
      setSelectedGuideId("");
      setDraft(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setStatusMessage("");

    const loadGuides = async () => {
      try {
        const [guidesResponse, unitsResponse] = await Promise.all([
          unitGuideAPI.getAll(courseId),
          unitAPI.getAll(courseId),
        ]);
        if (cancelled) return;
        setGuides(
          sortGuides(
            Array.isArray(guidesResponse.data) ? guidesResponse.data : []
          )
        );
        setCourseUnits(
          Array.isArray(unitsResponse.data) ? unitsResponse.data : []
        );
      } catch (loadError) {
        if (cancelled) return;
        setGuides([]);
        setCourseUnits([]);
        setSelectedGuideId("");
        setDraft(null);
        setError("Unable to load unit guides.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadGuides();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  const selectedGuide = useMemo(
    () => guides.find((g) => g.id === selectedGuideId) || null,
    [guides, selectedGuideId]
  );

  const suggestedUnitNumber = useMemo(
    () => suggestTemplateUnitNumber(courseUnits, guides),
    [courseUnits, guides]
  );

  // Auto-select first guide (or template when none exist)
  useEffect(() => {
    if (guides.length === 0) {
      setSelectedGuideId("");
      setDraft(buildTemplateDraft(courseId, suggestedUnitNumber));
      return;
    }
    if (!guides.some((g) => g.id === selectedGuideId)) {
      setSelectedGuideId(guides[0].id);
    }
  }, [guides, courseId, selectedGuideId, suggestedUnitNumber]);

  // Populate draft when selected guide changes
  useEffect(() => {
    if (!selectedGuide) {
      setDraft(null);
      return;
    }
    setDraft(toDraft(selectedGuide));
  }, [selectedGuide]);

  // ---- Draft helpers ----

  const setDraftField = (field, value) => {
    setDraft((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const handleReset = () => {
    if (selectedGuide) {
      setDraft(toDraft(selectedGuide));
      return;
    }
    setDraft(buildTemplateDraft(courseId, suggestedUnitNumber));
  };

  const setSectionField = (index, field, value) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const nextSections = [...prev.sections];
      nextSections[index] = { ...nextSections[index], [field]: value };
      return { ...prev, sections: nextSections };
    });
  };

  const addSection = () => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        sections: [
          ...prev.sections,
          {
            id: nextSectionId(prev.sections),
            heading: "",
            paragraphsText: "",
            bulletsText: "",
          },
        ],
      };
    });
  };

  const removeSection = (index) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        sections: prev.sections.filter((_, i) => i !== index),
      };
    });
  };

  const moveSection = (index, direction) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const target = index + direction;
      if (target < 0 || target >= prev.sections.length) return prev;
      const nextSections = [...prev.sections];
      const [picked] = nextSections.splice(index, 1);
      nextSections.splice(target, 0, picked);
      return { ...prev, sections: nextSections };
    });
  };

  const reloadGuides = async () => {
    const response = await unitGuideAPI.getAll(courseId);
    const rows = sortGuides(
      Array.isArray(response.data) ? response.data : []
    );
    setGuides(rows);
    return rows;
  };

  // ---- Save ----

  const handleSave = async () => {
    if (!draft) return;

    const parsedUnitNumber = Number(draft.unitNumber);
    if (!Number.isFinite(parsedUnitNumber) || parsedUnitNumber <= 0) {
      setError("Unit number must be a positive number.");
      return;
    }
    if (!draft.title.trim()) {
      setError("Title is required.");
      return;
    }
    if (
      (draft.startDate && !draft.endDate) ||
      (!draft.startDate && draft.endDate)
    ) {
      setError("Start and end dates must both be set or both be blank.");
      return;
    }
    if (
      draft.startDate &&
      draft.endDate &&
      draft.endDate < draft.startDate
    ) {
      setError("End date must be on or after start date.");
      return;
    }

    const normalizedSections = draft.sections
      .map((section) => ({
        id: section.id.trim(),
        heading: section.heading.trim(),
        paragraphs: textToList(section.paragraphsText),
        bullets: textToList(section.bulletsText),
      }))
      .filter((section) => section.id && section.heading);

    const sectionIds = normalizedSections.map((s) => s.id);
    if (new Set(sectionIds).size !== sectionIds.length) {
      setError("Section IDs must be unique.");
      return;
    }

    setSaving(true);
    setError("");
    setStatusMessage("");

    try {
      const payload = {
        courseId: draft.courseId || courseId,
        unitNumber: parsedUnitNumber,
        title: draft.title.trim(),
        subtitle: draft.subtitle.trim(),
        startDate: draft.startDate || null,
        endDate: draft.endDate || null,
        dateRange: draft.dateRange.trim(),
        totalDays: draft.totalDays.trim(),
        sourceLabel: draft.sourceLabel.trim(),
        sourceUrl: draft.sourceUrl.trim(),
        transcriptPath: draft.transcriptPath.trim(),
        sections: normalizedSections,
      };

      let persistedGuideId = selectedGuide?.id || "";
      if (selectedGuide) {
        await unitGuideAPI.update(selectedGuide.id, payload);
      } else {
        const createResponse = await unitGuideAPI.create(payload);
        persistedGuideId = createResponse.data?.id || "";
      }

      const rows = await reloadGuides();
      if (persistedGuideId && rows.some((row) => row.id === persistedGuideId)) {
        setSelectedGuideId(persistedGuideId);
      } else {
        setSelectedGuideId(rows[0]?.id || "");
      }
      setStatusMessage(selectedGuide ? "Unit guide saved." : "Unit guide created.");
    } catch (saveError) {
      setError(
        saveError?.response?.data?.error || "Failed to save unit guide."
      );
    } finally {
      setSaving(false);
    }
  };

  // ---- Render ----

  return (
    <div className="unit-guide-editor-body">
      <aside className="unit-guide-editor-list">
        {loading && <p>Loading guides...</p>}
        {!loading && guides.length === 0 && (
          <>
            <p>No unit guides found for this course.</p>
            <p className="unit-guide-template-hint">
              A starter template is loaded on the right. Update it and click{" "}
              <strong>Create Guide</strong>.
            </p>
            <p className="unit-guide-template-hint">
              Suggested unit number:{" "}
              <strong>{suggestedUnitNumber}</strong>
            </p>
          </>
        )}
        {!loading &&
          guides.map((guide) => (
            <button
              key={guide.id}
              type="button"
              className={`unit-guide-entry-btn ${
                guide.id === selectedGuideId ? "is-active" : ""
              }`}
              onClick={() => setSelectedGuideId(guide.id)}
            >
              <span>
                Unit {guide.unitNumber}: {guide.title}
              </span>
              <small>
                {formatDateRangePreview(
                  toDateInput(guide.startDate),
                  toDateInput(guide.endDate)
                ) ||
                  guide.dateRange ||
                  "Date range not set"}
              </small>
            </button>
          ))}
      </aside>

      <section className="unit-guide-editor-form">
        {!draft ? (
          <p>Select a unit guide to edit.</p>
        ) : (
          <>
            <div className="unit-guide-editor-grid">
              <label htmlFor="guide-unit-number">
                Unit Number
                <input
                  id="guide-unit-number"
                  type="number"
                  min="1"
                  value={draft.unitNumber}
                  onChange={(e) => setDraftField("unitNumber", e.target.value)}
                />
              </label>

              <label htmlFor="guide-total-days">
                Total Days Label
                <input
                  id="guide-total-days"
                  type="text"
                  value={draft.totalDays}
                  onChange={(e) => setDraftField("totalDays", e.target.value)}
                />
              </label>

              <label htmlFor="guide-title" className="unit-guide-editor-full">
                Title
                <input
                  id="guide-title"
                  type="text"
                  value={draft.title}
                  onChange={(e) => setDraftField("title", e.target.value)}
                />
              </label>

              <label
                htmlFor="guide-subtitle"
                className="unit-guide-editor-full"
              >
                Subtitle
                <input
                  id="guide-subtitle"
                  type="text"
                  value={draft.subtitle}
                  onChange={(e) => setDraftField("subtitle", e.target.value)}
                />
              </label>

              <label htmlFor="guide-date-range">
                Date Range Label
                <input
                  id="guide-date-range"
                  type="text"
                  value={draft.dateRange}
                  onChange={(e) => setDraftField("dateRange", e.target.value)}
                />
              </label>

              <label htmlFor="guide-start-date">
                Start Date
                <input
                  id="guide-start-date"
                  type="date"
                  value={draft.startDate}
                  onChange={(e) => setDraftField("startDate", e.target.value)}
                />
              </label>

              <label htmlFor="guide-end-date">
                End Date
                <input
                  id="guide-end-date"
                  type="date"
                  value={draft.endDate}
                  onChange={(e) => setDraftField("endDate", e.target.value)}
                />
              </label>

              <label htmlFor="guide-source-label">
                Source Label
                <input
                  id="guide-source-label"
                  type="text"
                  value={draft.sourceLabel}
                  onChange={(e) =>
                    setDraftField("sourceLabel", e.target.value)
                  }
                />
              </label>

              <label
                htmlFor="guide-source-url"
                className="unit-guide-editor-full"
              >
                Source URL
                <input
                  id="guide-source-url"
                  type="text"
                  value={draft.sourceUrl}
                  onChange={(e) => setDraftField("sourceUrl", e.target.value)}
                />
              </label>

              <label
                htmlFor="guide-transcript-path"
                className="unit-guide-editor-full"
              >
                Transcript Path
                <input
                  id="guide-transcript-path"
                  type="text"
                  value={draft.transcriptPath}
                  onChange={(e) =>
                    setDraftField("transcriptPath", e.target.value)
                  }
                />
              </label>
            </div>

            {/* Sections */}
            <div className="unit-guide-sections-header">
              <h4>Sections</h4>
              <button type="button" className="primary-btn" onClick={addSection}>
                Add Section
              </button>
            </div>

            <div className="unit-guide-sections-list">
              {draft.sections.length === 0 && (
                <p className="unit-guide-sections-empty">
                  No sections yet. Add a section to start.
                </p>
              )}
              {draft.sections.map((section, index) => (
                <article
                  key={`section-${index}`}
                  className="unit-guide-section-card"
                >
                  <div className="unit-guide-section-head">
                    <strong>Section {index + 1}</strong>
                    <div className="unit-guide-section-actions">
                      <button
                        type="button"
                        className="primary-btn"
                        onClick={() => moveSection(index, -1)}
                        disabled={index === 0}
                      >
                        Up
                      </button>
                      <button
                        type="button"
                        className="primary-btn"
                        onClick={() => moveSection(index, 1)}
                        disabled={index === draft.sections.length - 1}
                      >
                        Down
                      </button>
                      <button
                        type="button"
                        className="primary-btn"
                        onClick={() => removeSection(index)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <div className="unit-guide-section-grid">
                    <label htmlFor={`guide-section-id-${index}`}>
                      Section ID
                      <input
                        id={`guide-section-id-${index}`}
                        type="text"
                        value={section.id}
                        onChange={(e) =>
                          setSectionField(index, "id", e.target.value)
                        }
                      />
                    </label>

                    <label htmlFor={`guide-section-heading-${index}`}>
                      Heading
                      <input
                        id={`guide-section-heading-${index}`}
                        type="text"
                        value={section.heading}
                        onChange={(e) =>
                          setSectionField(index, "heading", e.target.value)
                        }
                      />
                    </label>

                    <label
                      htmlFor={`guide-section-paragraphs-${index}`}
                      className="unit-guide-editor-full"
                    >
                      Paragraphs (one per line)
                      <textarea
                        id={`guide-section-paragraphs-${index}`}
                        rows="3"
                        value={section.paragraphsText}
                        onChange={(e) =>
                          setSectionField(
                            index,
                            "paragraphsText",
                            e.target.value
                          )
                        }
                      />
                    </label>

                    <label
                      htmlFor={`guide-section-bullets-${index}`}
                      className="unit-guide-editor-full"
                    >
                      Bullets (one per line)
                      <textarea
                        id={`guide-section-bullets-${index}`}
                        rows="3"
                        value={section.bulletsText}
                        onChange={(e) =>
                          setSectionField(
                            index,
                            "bulletsText",
                            e.target.value
                          )
                        }
                      />
                    </label>
                  </div>
                </article>
              ))}
            </div>

            {/* Save / Reset */}
            <div className="unit-guide-editor-actions">
              <button
                type="button"
                className="primary-btn"
                title="Revert unsaved edits"
                disabled={saving || !draft}
                onClick={handleReset}
              >
                Reset
              </button>
              <button
                type="button"
                className="primary-btn"
                disabled={saving || !draft}
                onClick={handleSave}
              >
                {saving
                  ? "Saving..."
                  : selectedGuide
                  ? "Save Changes"
                  : "Create Guide"}
              </button>
            </div>
          </>
        )}

        {(error || statusMessage) && (
          <div className="unit-guide-editor-feedback">
            {error && <p className="unit-guide-editor-error">{error}</p>}
            {statusMessage && (
              <p className="unit-guide-editor-success">{statusMessage}</p>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default UnitGuideEditorContent;
